"use client";

import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Error({
  error,
  reset,
  retry,
}: {
  error: Error & { digest?: string };
  reset?: () => void;
  retry?: () => void;
}) {
  const [copiado, setCopiado] = useState(false);

  // Detalle técnico para que el usuario pueda pasarlo a soporte aunque ya no
  // pueda abrir la consola (el error desaparece al recargar).
  const detalle = [
    `Hora: ${new Date().toISOString()}`,
    `URL: ${typeof window !== "undefined" ? window.location.href : ""}`,
    `Error: ${error?.name ?? "Error"}: ${error?.message ?? String(error)}`,
    error?.digest ? `Digest: ${error.digest}` : null,
    error?.stack ? `Stack:\n${error.stack.split("\n").slice(0, 8).join("\n")}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  useEffect(() => {
    console.error("Error de pantalla:", error);

    // Tras un despliegue, una pestaña abierta con la versión anterior pide
    // ficheros (chunks) que ya no existen y TODA navegación falla hasta
    // recargar. Se recarga sola una vez (con freno de 30 s para no hacer bucle).
    const msg = `${error?.name ?? ""} ${error?.message ?? ""}`;
    const esChunkObsoleto =
      /ChunkLoadError|Loading chunk .* failed|Failed to fetch dynamically imported module|Failed to load chunk|error loading dynamically imported module/i.test(
        msg,
      );
    if (esChunkObsoleto) {
      const key = "error-boundary-chunk-reload";
      const last = Number(sessionStorage.getItem(key) || "0");
      if (Date.now() - last > 30_000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24 px-6 text-center">
      <div className="rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700 p-3">
        <AlertTriangle className="h-6 w-6 text-amber-600" aria-hidden="true" />
      </div>
      <div>
        <h1 className="font-heading text-xl font-semibold">No se ha podido cargar esta pantalla</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-md">
          Recarga para intentarlo de nuevo, o vuelve atrás.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="montmar"
          onClick={() => window.location.reload()}
        >
          Recargar
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            if (window.history.length > 1) window.history.back();
            else if (retry) retry();
            else if (reset) reset();
            else window.location.href = "/";
          }}
        >
          Volver
        </Button>
      </div>
      <details className="max-w-xl w-full text-left text-xs text-muted-foreground">
        <summary className="cursor-pointer text-center select-none">Detalles técnicos</summary>
        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3">
          {detalle}
        </pre>
        <div className="mt-2 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigator.clipboard
                ?.writeText(detalle)
                .then(() => setCopiado(true))
                .catch(() => setCopiado(false));
            }}
          >
            {copiado ? "Copiado" : "Copiar detalles"}
          </Button>
        </div>
      </details>
    </div>
  );
}
